/// <amd-module name="Wit/Common/WorkItemDialogs/Services/ChangeParentService" />
import { getTFSProject } from "Tfs/Platform/Page";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { WorkItem, WorkItemLink } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { BacklogLevelConfiguration, BacklogLevelWorkItems } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";

export interface IChangeParentService extends IVssService {
    getTeamId(): IReadonlyObservableValue<string>;
    getErrorText(): IReadonlyObservableValue<string>;
    getIsBusy(): IReadonlyObservableValue<boolean>;
    getParentBacklogLevelId(): IReadonlyObservableValue<string>;
    getTopLevelWorkItems(): IReadonlyObservableValue<INamedFieldDataDictionary[]>;
    getParentWorkItems(): IReadonlyObservableValue<WorkItem[]>;
    getNewParentId(): IReadonlyObservableValue<number | undefined>;
    setNewParentId(id: number): void;
    setWorkItems(workItems: INamedFieldDataDictionary[]): void;
    setTeamId(teamId: string): void;
    initialize(workItems: INamedFieldDataDictionary[], teamId: string): void;
}

class ChangeParentService extends VssService implements IChangeParentService {
    private readonly teamId = new ObservableValue<string>("");
    private readonly parentBacklogLevel = new ObservableValue<string>("");
    private readonly errorText = new ObservableValue<string>("");
    private readonly isBusy = new ObservableValue<boolean>(false);
    private readonly topLevelWorkItems = new ObservableValue<INamedFieldDataDictionary[]>([]);
    private readonly parentWorkItems = new ObservableValue<WorkItem[]>([]);
    private readonly newParentId = new ObservableValue<number | undefined>(undefined);
    private backlogLevels: { [teamId: string]: Promise<BacklogLevelConfiguration[]> } = {};
    private workItems: INamedFieldDataDictionary[] = [];
    private busyCount: number = 0;

    public getErrorText = (): IReadonlyObservableValue<string> => {
        return this.errorText;
    };

    public getTeamId = (): IReadonlyObservableValue<string> => {
        return this.teamId;
    };

    public getIsBusy = (): IReadonlyObservableValue<boolean> => {
        return this.isBusy;
    };

    public setTeamId = (teamId: string): void => {
        this.teamId.value = teamId;

        this.updateObservables();
    };

    public getParentBacklogLevelId = (): IReadonlyObservableValue<string> => {
        return this.parentBacklogLevel;
    };

    public getTopLevelWorkItems = (): IReadonlyObservableValue<INamedFieldDataDictionary[]> => {
        return this.topLevelWorkItems;
    };

    public getParentWorkItems = (): IReadonlyObservableValue<WorkItem[]> => {
        return this.parentWorkItems;
    };

    public initialize = (workItems: INamedFieldDataDictionary[], teamId: string): void => {
        this.increaseBusyCount();

        this.teamId.value = teamId;
        this.workItems = workItems;

        this.updateObservables();

        this.decreaseBusyCount();
    };

    public setWorkItems = (workItems: INamedFieldDataDictionary[]): void => {
        this.workItems = workItems;

        this.updateObservables();
    };

    public getNewParentId = (): IReadonlyObservableValue<number | undefined> => {
        return this.newParentId;
    };

    public setNewParentId = (id: number): void => {
        this.newParentId.value = id;
    };

    private updateObservables = () => {
        this.getBacklogLevels().then(levels => {
            this.updateTopLevelWorkItems().then(() => {
                this.updateParentWorkItems();
            });
        });
    };

    private updateParentWorkItems = async (): Promise<void> => {
        this.increaseBusyCount();

        const workItemService = this.pageContext.getService<IWorkItemService>("work-item-service");
        const projectId = getTFSProject(this.pageContext)?.id ?? "";
        const teamContext = {
            team: "",
            teamId: this.teamId.value,
            project: projectId,
            projectId: projectId
        };

        const workRestClient = getWorkClient(this.pageContext);

        try {
            const backlogWorkItems: BacklogLevelWorkItems = await workRestClient.getBacklogLevelWorkItems(teamContext, this.parentBacklogLevel.value);

            if (backlogWorkItems && backlogWorkItems.workItems) {
                const workItemIds = backlogWorkItems.workItems.map((workItemLink: WorkItemLink) => workItemLink.target.id);

                const fieldRefNames = [CoreFieldRefNames.TeamProject, CoreFieldRefNames.Title, CoreFieldRefNames.WorkItemType];
                const workItems: WorkItem[] = (await Promise.all(workItemService.loadWorkItems(projectId, workItemIds, fieldRefNames))).flat();

                this.parentWorkItems.value = workItems;
                this.newParentId.value = undefined;
            }
        } catch (error) {
            this.parentWorkItems.value = [];
            this.newParentId.value = undefined;
            this.errorText.value = format(Resources.ChangeParentErrorLoadingParentWorkItemsMessage, error);
        }

        this.decreaseBusyCount();
    };

    private getBacklogLevels = (): Promise<BacklogLevelConfiguration[]> => {
        if (this.teamId.value && !this.backlogLevels[this.teamId.value]) {
            this.increaseBusyCount();
            const workClient = getWorkClient(this.pageContext);
            const project = getTFSProject(this.pageContext);

            this.backlogLevels[this.teamId.value] = workClient
                .getBacklogs({ project: "", projectId: project?.id!, team: "", teamId: this.teamId.value })
                .then(value => value.sort((a, b) => b.rank - a.rank));
            this.decreaseBusyCount();
        }

        return this.backlogLevels[this.teamId.value];
    };

    private updateTopLevelWorkItems = async () => {
        this.increaseBusyCount();

        this.getBacklogLevels().then(
            backlogLevels => {
                let minBacklogLevelIndex = backlogLevels.length;

                let minimizingWorkItems: INamedFieldDataDictionary[] = [];

                for (const workItem of this.workItems) {
                    const workItemBacklogLevelIndex = indexOfConfigForWorkItemType(backlogLevels, workItem[CoreFieldRefNames.WorkItemType]);

                    if (workItemBacklogLevelIndex < minBacklogLevelIndex) {
                        minimizingWorkItems = [];
                        minBacklogLevelIndex = workItemBacklogLevelIndex;
                    }

                    if (workItemBacklogLevelIndex == minBacklogLevelIndex) {
                        minimizingWorkItems.push(workItem);
                    }
                }

                this.topLevelWorkItems.value = minimizingWorkItems;

                if (minBacklogLevelIndex > 0 && minBacklogLevelIndex < backlogLevels.length) {
                    this.parentBacklogLevel.value = backlogLevels[minBacklogLevelIndex - 1].id;
                } else {
                    this.parentBacklogLevel.value = "";
                    this.errorText.value = Resources.ChangeParentNoViableBacklogLevel;
                }
            },
            reason => {
                this.parentBacklogLevel.value = "";
                this.topLevelWorkItems.value = [];
                this.errorText.value = format(Resources.ChangeParentErrorLoadingParentWorkItemsMessage, reason);
            }
        );

        this.decreaseBusyCount();
    };

    private increaseBusyCount = () => {
        this.busyCount++;

        if (this.busyCount > 0) {
            this.isBusy.value = true;
        }
    };

    private decreaseBusyCount = () => {
        this.busyCount--;

        if (this.busyCount <= 0) {
            this.isBusy.value = false;
        }
    };
}

Services.add("IChangeParentService", { serviceFactory: ChangeParentService });

export function getChangeParentService(pageContext: IVssPageContext) {
    return pageContext.getService<IChangeParentService>("IChangeParentService");
}

function indexOfConfigForWorkItemType(configs: BacklogLevelConfiguration[], workItemType: string): number {
    for (let backlogIndex = 0; backlogIndex < configs.length; backlogIndex++) {
        for (let workItemTypeIndex = 0; workItemTypeIndex < configs[backlogIndex].workItemTypes.length; workItemTypeIndex++) {
            if (configs[backlogIndex].workItemTypes[workItemTypeIndex].name === workItemType) {
                return backlogIndex;
            }
        }
    }
    return configs.length;
}
